import React, { useState, useEffect } from 'react';

import { CropsHub } from './CropsHub';
import { LivestockHub } from './LivestockHub';

interface FarmOption {
  id: number;
  name: string;
}

export const PublicAgricHub: React.FC = () => {
  // 💾 State Management Layer
  const [shopId, setShopId] = useState<string | null>(() => localStorage.getItem('remembered_shop_id'));
  const [farmName, setFarmName] = useState<string>(() => localStorage.getItem('remembered_farm_name') || '');
  const [farmsList, setFarmsList] = useState<FarmOption[]>([]);
  const [view, setView] = useState<'login' | 'register' | 'dashboard'>(() => {
    return localStorage.getItem('remembered_session_name') ? 'dashboard' : 'login';
  });

  // Auth & Session Trackers
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [category, setCategory] = useState('farm_hand');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'poultry' | 'crops' | 'livestock'>('poultry');
  const [poultryView, setPoultryView] = useState<'menu' | 'production' | 'feed' | 'implements' | 'vaccination' | 'purchases' | 'sales'>('menu');
  const [userSession, setUserSession] = useState<{ name: string; role: string } | null>(() => {
    const cachedName = localStorage.getItem('remembered_session_name');
    const cachedRole = localStorage.getItem('remembered_session_role');
    return cachedName && cachedRole ? { name: cachedName, role: cachedRole } : null;
  });

  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [adjustFeedType, setAdjustFeedType] = useState('Starter Crumbs');
  const [adjustAction, setAdjustAction] = useState<'add' | 'remove'>('add');
  const [adjustQuantity, setAdjustQuantity] = useState('');
  const [adjustReason, setAdjustReason] = useState('Spillage/Waste');

  // 📊 Financial Reporting Layers State Management
  const [financials, setFinancials] = useState({
    total_revenue: 0,
    total_expenses: 0,
    net_profit: 0,
    sales_count: 0,
    purchase_count: 0
  });
  const [loadingFinancials, setLoadingFinancials] = useState(false);

  // 💰 Purchases Module Layout State Management
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [purchaseCategory, setPurchaseCategory] = useState('Feed');
  const [purchaseDescription, setPurchaseDescription] = useState('Starter Crumbs');
  const [purchaseQuantity, setPurchaseQuantity] = useState('');
  const [purchaseUnitPrice, setPurchaseUnitPrice] = useState('');
  const [purchaseSupplier, setPurchaseSupplier] = useState('');
  const [purchaseReceipt, setPurchaseReceipt] = useState('');

  // 📝 Log Daily Feeding Module States
  const [isFeedModalOpen, setIsFeedModalOpen] = useState(false);
  const [feedLogType, setFeedLogType] = useState('Starter Crumbs');
  const [feedLogQuantity, setFeedLogQuantity] = useState('');
  const [feedLogShed, setFeedLogShed] = useState('Shed 1');

  // 📜 Feed History Tracking State Layers
  interface FeedLogItem {
    id: string;
    feed_type: string;
    action_type: 'add' | 'remove';
    quantity_bags: number;
    adjustment_reason: string;
    logged_by_role: string;
    formatted_date: string;
  }

  const [feedHistory, setFeedHistory] = useState<FeedLogItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);

  // 💰 Sales Module State Tracking Layers
  const [isSalesModalOpen, setIsSalesModalOpen] = useState(false);
  const [salesProductType, setSalesProductType] = useState('Eggs');
  const [salesQuantity, setSalesQuantity] = useState('');
  const [salesUnitPrice, setSalesUnitPrice] = useState('');
  const [salesCustomer, setSalesCustomer] = useState('');
  const [salesPaymentMethod, setSalesPaymentMethod] = useState('Mobile Money');

  // 💉 Vaccination Module State Tracking Layers
  const [isVaccineModalOpen, setIsVaccineModalOpen] = useState(false);
  const [vaccineName, setVaccineName] = useState('Gumboro');
  const [vaccineFlockType, setVaccineFlockType] = useState('Broilers');
  const [vaccineMethod, setVaccineMethod] = useState('Drinking Water');
  const [vaccineDosage, setVaccineDosage] = useState('');
  const [vaccineBirdsTreated, setVaccineBirdsTreated] = useState('');
  const [vaccineScheduleDate, setVaccineScheduleDate] = useState('');

  const [feedBalances, setFeedBalances] = useState<Record<string, number>>({
    'Starter Crumbs': 0,
    'Growers Pellets': 0,
    'Finisher Pellets': 0,
    'Layers Mash': 0
  });
  const [loadingBalances, setLoadingBalances] = useState<boolean>(false);

  // 📋 Production Modal Controls
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalProduct, setModalProduct] = useState('Broiler');
  const [chickCount, setChickCount] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [productionDays, setProductionDays] = useState(42); // Default Broiler timeframe
  const [maturityDate, setMaturityDate] = useState('');

  // 🔄 Automated Date Calculators matching the reference image layout specifications
  useEffect(() => {
    if (startDate && productionDays) {
      const start = new Date(startDate);
      start.setDate(start.getDate() + Number(productionDays));
      setMaturityDate(start.toISOString().split('T')[0]);
    }
  }, [startDate, productionDays]);

  // Adjust standard production targets instantly when product dropdown value toggles
  const handleProductChange = (type: string) => {
    setModalProduct(type);
    if (type === 'Broiler') setProductionDays(42);
    else if (type === 'Layers') setProductionDays(140); // Standard point of lay timeframe
    else if (type === 'Kenbro') setProductionDays(84);
    else if (type === 'Geese') setProductionDays(180);
    else if (type === 'Turkey') setProductionDays(150);
  };


  // 🌽 Crops Production Modal States
  const [isCropsModalOpen, setIsCropsModalOpen] = useState(false);
  const [cropClass, setCropClass] = useState<'Vegetables' | 'Fruits' | 'Tubers' | 'Grains'>('Vegetables');
  const [cropVariety, setCropVariety] = useState('Spinach');
  const [acreage, setAcreage] = useState('');
  const [cropStartDate, setCropStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [harvestDate, setHarvestDate] = useState('');

  // 🔄 Automated Crop Harvest Timeline Predictor 
  useEffect(() => {
    if (cropStartDate) {
      const start = new Date(cropStartDate);
      let growingDays = 90; // Default fallback duration layout
      
      // Auto-compute average duration timelines matching standard local varieties
      if (cropVariety === 'Spinach' || cropVariety === 'Sukuma Wiki') growingDays = 60;
      else if (cropVariety === 'Tomato') growingDays = 90;
      else if (cropVariety === 'Avocado' || cropVariety === 'Mango') growingDays = 365 * 3; // Long-term orchard trees
      else if (cropVariety === 'Potatoes') growingDays = 105;
      else if (cropVariety === 'Carrots') growingDays = 90;
      else if (cropVariety === 'Mhogo') growingDays = 270;

      start.setDate(start.getDate() + growingDays);
      setHarvestDate(start.toISOString().split('T')[0]);
    }
  }, [cropStartDate, cropVariety]);

  // Handle cascading dropdown state values cleanly
  const handleCropClassChange = (value: 'Vegetables' | 'Fruits' | 'Tubers') => {
    setCropClass(value);
    if (value === 'Vegetables') setCropVariety('Spinach');
    else if (value === 'Fruits') setCropVariety('Avocado');
    else if (value === 'Tubers') setCropVariety('Potatoes');
  };

  // 🐄 Livestock Production Modal States
  const [isLiveModalOpen, setIsLiveModalOpen] = useState(false);
  const [animalClass, setAnimalClass] = useState<'Cattle' | 'Goats' | 'Sheep'>('Cattle');
  const [breedVariety, setBreedVariety] = useState('Friesian');
  const [headCount, setHeadCount] = useState('');
  const [healthStatus, setHealthStatus] = useState('Healthy');

  // Handle cascading dropdown states cleanly for common local breeds
  const handleAnimalClassChange = (value: 'Cattle' | 'Goats' | 'Sheep') => {
    setAnimalClass(value);
    if (value === 'Cattle') setBreedVariety('Friesian');
    else if (value === 'Goats') setBreedVariety('Boer');
    else if (value === 'Sheep') setBreedVariety('Dorper');
  };

  // Fetch active agri-tenants on initialization
  useEffect(() => {
    if (!shopId) {
      async function fetchActiveFarms() {
        try {
          const response = await fetch('https://n8n.tenear.com/webhook/fetch-farms', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ business_category: 'agricultural' })
          });
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            setFarmsList(data);
            setShopId(data[0].id.toString());
            setFarmName(data[0].name);
          }
        } catch (err) {
          console.error("Failed loading farms context", err);
        }
      }
      fetchActiveFarms();
    }
  }, [shopId]);

  const handleFarmDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const match = farmsList.find(f => f.id.toString() === selectedId);
    if (match) {
      setShopId(selectedId);
      setFarmName(match.name);
    }
  };

  const handleAuth = async (e: React.FormEvent, type: 'login' | 'register') => {
    e.preventDefault();
    if (!shopId) return alert("Select farm workspace.");
    setLoading(true);
    const combinedFullName = `${firstName.trim()} ${lastName.trim()}`;
    const payload = type === 'register'
      ? { action: 'register', shop_id: parseInt(shopId), phone_number: phone, password, full_name: combinedFullName, user_category: category }
      : { action: 'login', shop_id: parseInt(shopId), phone_number: phone, password };

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/sign-farmer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok && data.success && data.user) {
        localStorage.setItem('remembered_shop_id', shopId);
        localStorage.setItem('remembered_farm_name', farmName);
        localStorage.setItem('remembered_session_name', data.user.full_name);
        localStorage.setItem('remembered_session_role', data.user.user_category);
        localStorage.setItem('remembered_phone_number', phone);
        setUserSession({ name: data.user.full_name, role: data.user.user_category });
        setView('dashboard');
      } else {
        alert(data.message || "Auth match error.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 💾 Commit New Cycle Records to n8n Gateway Backend
  const handleSaveCycle = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/save-poultry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'save_poultry_cycle',
          shop_id: parseInt(shopId || '81'),
          phone_number: localStorage.getItem('remembered_phone_number') || '0700000000',
          bird_type: modalProduct,
          quantity: parseInt(chickCount),
          start_date: startDate,
          maturity_date: maturityDate,
          production_days: productionDays
        })
      });

      if (response.ok) {
        alert(`Production entry sequence logged successfully for ${chickCount} ${modalProduct}s!`);
        setIsModalOpen(false);
        setChickCount('');
      } else {
        alert("Failed to commit cycle data metrics.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const clearRememberedFarm = () => {
    localStorage.clear();
    setShopId(null);
    setFarmName('');
    setUserSession(null);
    setView('login');
  };

  const handleAdjustInventory = async (e: React.FormEvent) => {
    e.preventDefault();
  
    if (!adjustQuantity || parseFloat(adjustQuantity) <= 0) {
      alert("Please enter a valid quantity amount");
      return;
    }

    setLoading(true);

    // Structural payload matching your Cloudflare multi-tenant token context
    const adjustmentPayload = {
      shop_id: shopId,
      farm_name: farmName,
      feed_type: adjustFeedType,
      action_type: adjustAction, // 'add' increments stock, 'remove' decrements stock
      quantity_bags: parseFloat(adjustQuantity),
      adjustment_reason: adjustReason,
      logged_by_role: userSession?.role || 'farm_hand',
      timestamp: new Date().toISOString()
    };

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/adjust-poultry-inventory', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // If your platform passes an auth token or signature via headers, include it here:
          // 'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}` 
        },
        body: JSON.stringify(adjustmentPayload),
      });

      if (!response.ok) {
        throw new Error(`Server responded with operational status code: ${response.status}`);
      }

      // Optional: Parse JSON if your n8n workflow returns a confirmation object message
      // const responseData = await response.json();

      alert(`Successfully logged inventory update for ${adjustFeedType}!`);
    
      // Clear functional form state variables & dismiss overlay view
      setAdjustQuantity('');
      setIsAdjustModalOpen(false);

      // TODO: Refresh your stock listing component data here if you pull data from a state engine

    } catch (error) {
      console.error("Inventory adjustment pipeline network failure:", error);
      alert("Failed to submit inventory adjustment. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedBalancesFromN8N = async () => {
    if (!shopId) return;

    setLoadingBalances(true);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/fetch-feed-balance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_id: shopId })
      });

      if (!response.ok) throw new Error(`HTTP fetch error code: ${response.status}`);

      const data = await response.json();
    
      // Establish fresh zero baselines
      const updatedBalances: Record<string, number> = {
        'Starter Crumbs': 0,
        'Growers Pellets': 0,
        'Finisher Pellets': 0,
        'Layers Mash': 0
      };

      // Expecting n8n to return an array of rows: [{ feed_type: '...', current_balance_bags: 10 }]
      if (Array.isArray(data)) {
        data.forEach((row: { feed_type: string; current_balance_bags: number | string }) => {
          if (row.feed_type in updatedBalances) {
            updatedBalances[row.feed_type] = typeof row.current_balance_bags === 'string' 
              ? parseFloat(row.current_balance_bags) 
              : row.current_balance_bags;
          }
        });
      }

      setFeedBalances(updatedBalances);
    } catch (err) {
      console.error('Error executing n8n feed balance fetch route:', err);
    } finally {
      setLoadingBalances(false);
    }
  };


  // 🛠️ FIX: Unified Frontend Lifecycle Trigger
  useEffect(() => {
    // Triggers the webhook whenever the user opens the Feed view
    if (poultryView === 'feed' && shopId) {
      fetchFeedBalancesFromN8N();
      fetchFeedHistoryFromN8N(); // 🔄 Fetches history simultaneously
    }
  }, [poultryView, shopId]);

  const handleLogDailyFeeding = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!feedLogQuantity || parseFloat(feedLogQuantity) <= 0) {
      alert("Please enter a valid feed volume amount");
      return;
    }

    setLoading(true);

    const feedingPayload = {
      shop_id: shopId,
      farm_name: farmName,
      feed_type: feedLogType,
      quantity_bags: parseFloat(feedLogQuantity),
      shed_identifier: feedLogShed,
      logged_by_role: userSession?.role || 'farm_hand',
      timestamp: new Date().toISOString()
    };

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/log-daily-feed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedingPayload)
      });

      if (!response.ok) throw new Error(`Operational HTTP Error: ${response.status}`);

      alert(`Successfully logged consumption run for ${feedLogType}!`);
    
      // Clear inputs and dismiss modal layout
      setFeedLogQuantity('');
      setIsFeedModalOpen(false);
    
      // Refresh your balances display view right away via n8n pipeline loop
      fetchFeedBalancesFromN8N();

    } catch (error) {
      console.error("Consumption pipeline failure:", error);
      alert("Network transmission error logging feed metrics.");
    } finally {
      setLoading(false);
    }
  };


  const handleLogPurchase = async (e: React.FormEvent) => {
    e.preventDefault();

    const qty = parseFloat(purchaseQuantity);
    const price = parseFloat(purchaseUnitPrice);

    if (!qty || qty <= 0 || !price || price <= 0) {
      alert("Please enter valid positive quantity and pricing metrics");
      return;
    }

    setLoading(true);

    const purchasePayload = {
      shop_id: shopId,
      farm_name: farmName,
      item_category: purchaseCategory,
      item_description: purchaseDescription,
      quantity: qty,
      unit_price: price,
      total_cost: qty * price, // Automatically compiled financial matrix
      supplier_name: purchaseSupplier || 'Generic Supplier',
      receipt_reference: purchaseReceipt || 'N/A',
      logged_by_role: userSession?.role || 'farm_hand',
      timestamp: new Date().toISOString()
    };

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/poultry-feed-purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(purchasePayload)
      });

      if (!response.ok) throw new Error(`HTTP Error Status: ${response.status}`);

      alert(`Successfully logged purchase transaction for ${purchaseDescription}!`);
    
      // Reset Form Input Layout Buffers
      setPurchaseQuantity('');
      setPurchaseUnitPrice('');
      setPurchaseSupplier('');
      setPurchaseReceipt('');
      setIsPurchaseModalOpen(false);

    } catch (error) {
      console.error("Purchase processing transmission pipeline failure:", error);
      alert("Network transaction failure sending expense report.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogVaccination = async (e: React.FormEvent) => {
    e.preventDefault();

    const count = parseInt(vaccineBirdsTreated);
    const dose = parseFloat(vaccineDosage);

    if (!count || count <= 0 || !dose || dose <= 0 || !vaccineScheduleDate) {
      alert("Please fill in all medical tracking metrics correctly.");
      return;
    }

    setLoading(true);

    const vaccinePayload = {
      shop_id: shopId,
      farm_name: farmName,
      vaccine_name: vaccineName,
      flock_type: vaccineFlockType,
      administration_method: vaccineMethod,
      dosage_per_bird: dose,
      birds_treated: count,
      scheduled_date: vaccineScheduleDate,
      logged_by_role: userSession?.role || 'farm_hand',
      timestamp: new Date().toISOString()
    };

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/log-poultry-vaccine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vaccinePayload)
      });

      if (!response.ok) throw new Error(`HTTP Operational Error: ${response.status}`);

      alert(`Successfully logged vaccination record for ${vaccineName}!`);
    
      // Clear Input States & Close Modal Panel Context
      setVaccineDosage('');
      setVaccineBirdsTreated('');
      setVaccineScheduleDate('');
      setIsVaccineModalOpen(false);

    } catch (error) {
      console.error("Vaccination transmission pipeline failure:", error);
      alert("Network failure processing healthcare log.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogSale = async (e: React.FormEvent) => {
    e.preventDefault();

    const qty = parseFloat(salesQuantity);
    const price = parseFloat(salesUnitPrice);

    if (!qty || qty <= 0 || !price || price <= 0) {
      alert("Please enter valid positive quantity and pricing metrics.");
      return;
    }

    setLoading(true);

    const salesPayload = {
      shop_id: shopId,
      farm_name: farmName,
      product_type: salesProductType,
      quantity: qty,
      unit_price: price,
      total_revenue: qty * price, // Automatically compiled revenue metric
      customer_name: salesCustomer || 'Cash Customer',
      payment_method: salesPaymentMethod,
      logged_by_role: userSession?.role || 'farm_hand',
      timestamp: new Date().toISOString()
    };

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/poultry-sales', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(salesPayload)
      });

      if (!response.ok) throw new Error(`HTTP Operational Error: ${response.status}`);

      alert(`Successfully recorded sale for ${salesProductType}!`);
    
      // Clear Input States & Close Modal Panel Context
      setSalesQuantity('');
      setSalesUnitPrice('');
      setSalesCustomer('');
      setIsSalesModalOpen(false);

    } catch (error) {
      console.error("Sales transmission pipeline failure:", error);
      alert("Network transmission error logging sales metrics.");
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialReports = async () => {
    if (!shopId) return;
    setLoadingFinancials(true);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/get-poultry-financial-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_id: shopId })
      });

      if (!response.ok) throw new Error(`HTTP Error Status: ${response.status}`);
      const data = await response.json();
    
      if (data && !data.error) {
        setFinancials({
          total_revenue: parseFloat(data.total_revenue) || 0,
          total_expenses: parseFloat(data.total_expenses) || 0,
          net_profit: parseFloat(data.net_profit) || 0,
          sales_count: parseInt(data.sales_count) || 0,
          purchase_count: parseInt(data.purchase_count) || 0
        });
      }
    } catch (err) {
      console.error("Financial analytics fetch failed:", err);
    } finally {
      setLoadingFinancials(false);
    }
  };

  // 🔄 Auto-fetch metrics whenever the user lands on the poultry sub-menu dashboard
  useEffect(() => {
    if (activeTab === 'poultry' && poultryView === 'menu' && shopId) {
      fetchFinancialReports();
    }
  }, [activeTab, poultryView, shopId]);

  const fetchFeedHistoryFromN8N = async () => {
    if (!shopId) return;
    setLoadingHistory(true);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/get-poultry-feed-history', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_id: shopId })
      });

      if (!response.ok) throw new Error(`HTTP Error Status: ${response.status}`);
      const data = await response.json();
    
      if (Array.isArray(data)) {
        setFeedHistory(data);
      }
    } catch (err) {
      console.error("Feed ledger history fetch failure:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 p-4 text-slate-800 font-sans max-w-md mx-auto flex flex-col justify-start relative">
      
      {view !== 'dashboard' && (
        <div className="bg-emerald-950 p-4 rounded-2xl text-white mb-6 flex justify-between items-center shadow-xs">
          <div>
            <h3 className="text-[10px] uppercase tracking-wider opacity-60">System Target</h3>
            <h2 className="text-md font-bold">{farmName || 'Loading Setup...'}</h2>
          </div>
        </div>
      )}

      {/* LOGIN & REGISTRATION SECTIONS REMAIN UNCHANGED FOR AUTHENTICATION PASSES */}
      {view === 'login' && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs my-auto">
          <h2 className="text-xl font-black text-slate-900 mb-6">Sign In</h2>
          <form onSubmit={(e) => handleAuth(e, 'login')} className="space-y-4">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Phone Number</label>
              <input type="tel" placeholder="0716300197" value={phone} onChange={e => setPhone(e.target.value)} required className="w-full p-3 border border-slate-200 rounded-xl text-sm" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Password</label>
              <input type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required className="w-full p-3 border border-slate-200 rounded-xl text-sm" />
            </div>
            <button type="submit" className="w-full bg-emerald-600 text-white p-3.5 rounded-xl font-bold text-sm">Access Dashboard</button>
          </form>
          <p className="text-xs text-center text-slate-500 mt-6">New user? <button onClick={() => setView('register')} className="text-emerald-600 font-bold underline">Register Account</button></p>
        </div>
      )}

      {view === 'register' && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
          <h2 className="text-xl font-black text-emerald-800 mb-4">Worker Registration</h2>
          <form onSubmit={(e) => handleAuth(e, 'register')} className="space-y-4">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Select Farm Environment</label>
              <select value={shopId || ''} onChange={handleFarmDropdownChange} className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-white font-bold">
                {farmsList.map(farm => (
                  <option key={farm.id} value={farm.id}>{farm.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="text" placeholder="First Name" value={firstName} onChange={e => setFirstName(e.target.value)} required className="p-3 border border-slate-200 rounded-xl text-sm" />
              <input type="text" placeholder="Last Name" value={lastName} onChange={e => setLastName(e.target.value)} required className="p-3 border border-slate-200 rounded-xl text-sm" />
            </div>
            <input type="tel" placeholder="WhatsApp Phone Line" value={phone} onChange={e => setPhone(e.target.value)} required className="w-full p-3 border border-slate-200 rounded-xl text-sm" />
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase block mb-1">WhatsApp Mobile Line</label>
              <input 
                type="tel" 
                placeholder="0716300197" 
                value={phone} 
                onChange={e => setPhone(e.target.value)} 
                required 
                className="w-full p-3 border border-slate-200 rounded-xl text-sm text-slate-800 font-medium" 
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Corporate Assignment Role</label>
              <select 
                value={category} 
                onChange={e => setCategory(e.target.value)} 
                className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-white font-bold text-slate-700"
              >
                <option value="farm_hand">Operational Farm Hand</option>
                <option value="manager">Farm Field Manager</option>
                <option value="owner">Strategic Farm Owner</option>
                <option value="vet">Veterinary Medical Officer</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Create Access Password</label>
              <input 
                type="password" 
                placeholder="Min 6 alphanumeric characters" 
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                required 
                className="w-full p-3 border border-slate-200 rounded-xl text-sm text-slate-800" 
              />
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white p-3.5 rounded-xl font-bold text-sm tracking-wide transition-all shadow-xs"
            >
              {loading ? 'Committing Profile Space...' : 'Complete System Sign-On'}
            </button>
          </form>
          <p className="text-xs text-center text-slate-500 mt-4">
            Already registered? <button onClick={() => setView('login')} className="text-emerald-600 font-bold underline">Login Instead</button>
          </p>
        </div>
      )}

      {/* VIEW C: LIVE DATA METRICS DISPLAY CONSOLE */}
      {view === 'dashboard' && (
        <div className="w-full flex-grow animate-fadeIn">
          <div className="bg-emerald-800 text-white rounded-2xl p-4 mb-4 shadow-sm flex justify-between items-center">
            <div>
              <h1 className="text-md font-bold tracking-wide">{farmName || 'TeNEAR Agri-Control'}</h1>
              <p className="text-[11px] opacity-80">User: <span className="font-semibold">{userSession?.name}</span></p>
            </div>
            <button onClick={clearRememberedFarm} className="text-[10px] bg-emerald-950 font-bold px-2 py-1.5 rounded-lg">
              Log Out
            </button>
          </div>

          {/* Primary Operations Selector Tabs */}
          <div className="grid grid-cols-3 gap-2 mb-5">
            {(['poultry', 'crops', 'livestock'] as const).map((tab) => (
              <button 
                key={tab} 
                onClick={() => {
                  setActiveTab(tab);
                  setPoultryView('menu');
                }} 
                className={`py-2.5 px-1 rounded-xl font-bold text-xs capitalize transition-all border ${
                  activeTab === tab ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* 📋 Role-Based Operations Panel Action Strips */}
          {userSession?.role === 'farm_hand' ? (
            <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-2xl mb-4 text-xs text-emerald-900 flex justify-between items-center shadow-xs">
              <span className="font-semibold">📋 Daily Tasks Assignment</span>
              <span className="bg-emerald-600 text-white font-bold px-2 py-0.5 rounded text-[10px]">Active</span>
            </div>
          ) : ['owner', 'manager', 'vet'].includes(userSession?.role || '') ? (
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-2xl mb-4 text-xs text-blue-900 flex justify-between items-center shadow-xs">
              <span className="font-semibold">🚨 Open Farm Alerts</span>
              <span className="bg-blue-600 text-white font-bold px-2 py-0.5 rounded text-[10px]">All Clear</span>
            </div>
          ) : null}

          {/* 🐓 POULTRY MANAGEMENT HUB PANELS */}
          {activeTab === 'poultry' && poultryView === 'menu' && (
            <div className="space-y-3 animate-fadeIn">
              {/* Menu Intro Header */}
              <div className="bg-white border border-slate-200/80 p-4 rounded-2xl shadow-xs">
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-wide">Poultry Operations</h3>
                <p className="text-[10px] text-slate-400">Select an operational sub-sector to manage your poultry records</p>
              </div>

              {/* 📊 LIVE STRATEGIC FINANCIAL SCORECARDS */}
              <div className="grid grid-cols-3 gap-2 mt-2">
                
                {/* Revenue Card */}
                <div className="bg-white border border-slate-200/80 p-3 rounded-2xl shadow-xs text-left">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Revenue</span>
                  <div className="flex items-baseline space-x-1 mt-0.5">
                    <span className="text-xs font-black text-emerald-600">
                      {loadingFinancials ? '...' : `KES ${financials.total_revenue.toLocaleString()}`}
                    </span>
                  </div>
                  <span className="text-[8px] font-bold text-slate-400 block mt-0.5">{financials.sales_count} Sales logged</span>
                </div>

                {/* Expenses Card */}
                <div className="bg-white border border-slate-200/80 p-3 rounded-2xl shadow-xs text-left">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Expenses</span>
                  <div className="flex items-baseline space-x-1 mt-0.5">
                    <span className="text-xs font-black text-rose-600">
                      {loadingFinancials ? '...' : `KES ${financials.total_expenses.toLocaleString()}`}
                    </span>
                  </div>
                  <span className="text-[8px] font-bold text-slate-400 block mt-0.5">{financials.purchase_count} Bills paid</span>
                </div>

                {/* Net Profit Margin Card */}
                <div className={`border p-3 rounded-2xl shadow-xs text-left transition-all KES{
                  financials.net_profit >= 0 ? 'bg-emerald-50/40 border-emerald-200/60' : 'bg-rose-50/40 border-rose-200/60'
                }`}>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Net Margin</span>
                  <div className="flex items-baseline space-x-1 mt-0.5">
                    <span className={`text-xs font-black ${financials.net_profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {loadingFinancials ? '...' : `KES ${financials.net_profit.toLocaleString()}`}
                    </span>
                  </div>
                  <span className={`text-[8px] font-black uppercase ${financials.net_profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {financials.net_profit >= 0 ? '📈 Profit' : '📉 Loss'}
                  </span>
                </div>

              </div>

              {/* Functional Dashboard Hub Options */}
              <div className="grid grid-cols-1 gap-3">
                {/* 1. Poultry Production Menu Trigger */}
                <button
                  onClick={() => setPoultryView('production')}
                  className="w-full bg-white p-4 rounded-2xl border border-slate-200/60 shadow-xs flex justify-between items-center text-left hover:border-slate-300 transition-colors group"
                >
                  <div>
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider group-hover:text-blue-600 transition-colors">
                      Poultry Production
                    </h4>
                    <p className="text-[11px] text-slate-400 font-medium mt-0.5">Manage production cycles & flock segments</p>
                  </div>
                  <span className="text-lg">🐣</span>
                </button>

                {/* 2. Feed Management Menu Trigger */}
                <button
                  onClick={() => setPoultryView('feed')}
                  className="w-full bg-white p-4 rounded-2xl border border-slate-200/60 shadow-xs flex justify-between items-center text-left hover:border-slate-300 transition-colors group"
                >
                  <div>
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider group-hover:text-blue-600 transition-colors">
                      Feed Management
                    </h4>
                    <p className="text-[11px] text-slate-400 font-medium mt-0.5">Track feed stock, formulations & consumption</p>
                  </div>
                  <span className="text-lg">🌾</span>
                </button>

                {/* 3. Implements Menu Trigger */}
                <button
                  onClick={() => setPoultryView('implements')}
                  className="w-full bg-white p-4 rounded-2xl border border-slate-200/60 shadow-xs flex justify-between items-center text-left hover:border-slate-300 transition-colors group"
                >
                  <div>
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider group-hover:text-blue-600 transition-colors">
                      Implements
                    </h4>
                    <p className="text-[11px] text-slate-400 font-medium mt-0.5">Inventory of feeders, drinkers, & farm equipment</p>
                  </div>
                  <span className="text-lg">🛠️</span>
                </button>

                {/* 4. Vaccination Menu Trigger */}
                <button
                  onClick={() => setPoultryView('vaccination')}
                  className="w-full bg-white p-4 rounded-2xl border border-slate-200/60 shadow-xs flex justify-between items-center text-left hover:border-slate-300 transition-colors group"
                >
                  <div>
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider group-hover:text-blue-600 transition-colors">
                      Vaccination
                    </h4>
                    <p className="text-[11px] text-slate-400 font-medium mt-0.5">Schedule drug administration & health tracking</p>
                  </div>
                  <span className="text-lg">💉</span>
                </button>

                {/* 5. Purchases Menu Trigger */}
                <button
                  onClick={() => setPoultryView('purchases')}
                  className="w-full bg-white p-4 rounded-2xl border border-slate-200/60 shadow-xs flex justify-between items-center text-left hover:border-slate-300 transition-colors group"
                >
                  <div>
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider group-hover:text-blue-600 transition-colors">
                      Purchases
                    </h4>
                    <p className="text-[11px] text-slate-400 font-medium mt-0.5">Record Purchases</p>
                  </div>
                  <span className="text-lg">💰</span>
                </button>

                {/* 6. Sales Menu Trigger */}
                <button
                  onClick={() => setPoultryView('sales')}
                  className="w-full bg-white p-4 rounded-2xl border border-slate-200/60 shadow-xs flex justify-between items-center text-left hover:border-slate-300 transition-colors group"
                >
                  <div>
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider group-hover:text-blue-600 transition-colors">
                      Sales
                    </h4>
                    <p className="text-[11px] text-slate-400 font-medium mt-0.5">Record egg distribution & meat batch sales</p>
                  </div>
                  <span className="text-lg">💰</span>
                </button>
              </div>
            </div>
          )}

          {/* 🐣 SUB-MENU VIEW: POULTRY PRODUCTION */}
          {activeTab === 'poultry' && poultryView === 'production' && (
            <div className="space-y-4 animate-fadeIn">
              {/* Back to Top Level Menu Ribbon */}
              <button
                onClick={() => setPoultryView('menu')}
                className="text-[11px] text-blue-600 hover:text-blue-700 font-black tracking-wide uppercase flex items-center gap-1 transition-all"
              >
                ← Back to Poultry Overview
              </button>

              <div className="bg-white border border-slate-200/80 p-4 rounded-2xl flex justify-between items-center shadow-xs">
                <div>
                  <h3 className="text-xs font-black text-slate-900 uppercase tracking-wide">Poultry Production</h3>
                  <p className="text-[10px] text-slate-400">Log active farm cycles and flock segments</p>
                </div>
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-[11px] tracking-wide py-2 px-3.5 rounded-xl transition-all shadow-xs"
                >
                  + NEW CYCLE
                </button>
              </div>

              {/* Dynamic rendering loop matching your original card elements */}
              <div className="grid grid-cols-1 gap-3">
                {['Broilers', 'Layers', 'Kenbros', 'Geese', 'Turkeys'].map((cls) => (
                  <div key={cls} className="bg-white p-4 rounded-2xl border border-slate-200/60 shadow-xs flex justify-between items-center">
                    <div>
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">
                        {cls === 'Layers' ? 'Layerss' : cls === 'Geese' ? 'Geeses' : cls}
                      </h4>
                      <p className="text-[11px] text-slate-400 font-medium mt-0.5">No Active Cycles</p>
                    </div>
                    <span className="text-lg">
                      {cls === 'Broilers' || cls === 'Layers' || cls === 'Kenbros' ? '🐤' : '🪶'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 🌾 SUB-VIEW: FEED MANAGEMENT */}
          {activeTab === 'poultry' && poultryView === 'feed' && (
            <div className="space-y-4 animate-fadeIn">
              {/* Navigation Back Ribbon */}
              <button
                onClick={() => setPoultryView('menu')}
                className="text-[11px] text-blue-600 hover:text-blue-700 font-black tracking-wide uppercase flex items-center gap-1 transition-all"
              >
                ← Back to Poultry Overview
              </button>

              {/* Module Header */}
              <div className="bg-white border border-slate-200/80 p-4 rounded-2xl flex justify-between items-center shadow-xs">
                <div>
                  <h3 className="text-xs font-black text-slate-900 uppercase tracking-wide">Feed Management</h3>
                  <p className="text-[10px] text-slate-400">Track feed silo levels, daily bird ratios, and flock consumption</p>
                </div>
              </div>

              {/* ⚡ Quick Operational Actions */}
              <div className="grid grid-cols-2 gap-2">
                <button 
                  onClick={() => setIsFeedModalOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-[11px] tracking-wide py-3 px-2 rounded-xl transition-all shadow-xs text-center uppercase"
                >
                  📝 Log Daily Feeding
                </button>
                <button 
                  onClick={() => setIsAdjustModalOpen(true)}
                  className="bg-slate-800 hover:bg-slate-900 text-white font-extrabold text-[11px] tracking-wide py-3 px-2 rounded-xl transition-all shadow-xs text-center uppercase"
                >
                  ⚖️ Adjust Inventory
                </button>
              </div>

              {/* 📦 Live Stock Silo Inventory */}
              <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Current Stock Balance</h4>
                  {loadingBalances && <span className="text-[9px] font-bold text-blue-600 animate-pulse uppercase">Refreshing...</span>}
                </div>
                
                <div className="space-y-2">
                  {[
                    { type: 'Starter Crumbs' },
                    { type: 'Growers Pellets' },
                    { type: 'Finisher Pellets' },
                    { type: 'Layers Mash' }
                  ].map((feed) => {
                    // 🔄 FIX: Safely pull the calculated value directly from your live n8n state variable
                    const balance = feedBalances[feed.type] !== undefined ? feedBalances[feed.type] : 0;
                    
                    // Dynamically calculate stock alert badges based on live calculated levels
                    let status = 'Optimal';
                    let colorCode = 'text-emerald-600 bg-emerald-50';
                    
                    if (balance <= 0) {
                      status = 'Out of Stock';
                      colorCode = 'text-rose-600 bg-rose-50';
                    } else if (balance <= 5) {
                      status = 'Low Stock';
                      colorCode = 'text-amber-600 bg-amber-50';
                    }

                    return (
                      <div key={feed.type} className="flex justify-between items-center p-3 bg-slate-50/50 rounded-xl border border-slate-100">
                        <div>
                          <p className="text-xs font-black text-slate-800">{feed.type}</p>
                          <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                            Available Volume: <span className="font-bold text-slate-700">{balance} Bags</span>
                          </p>
                        </div>
                        <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-md ${colorCode}`}>
                          {status}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 📊 Advanced Analytics: Feed Conversion Helper */}
              <div className="bg-blue-50/50 border border-blue-100 p-4 rounded-2xl shadow-xs text-left">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-xs">📊</span>
                  <h4 className="text-[11px] font-black text-blue-900 uppercase tracking-wide">FCR Calculator Insight</h4>
                </div>
                <p className="text-[10px] text-slate-500 leading-relaxed font-medium">
                  Your Feed Conversion Ratio measures how efficiently birds convert feed into weight/eggs. Daily feeding entry cards calculate this metric against your current active batch counts automatically.
                </p>
              </div>

              {/* 📜 Dynamic Feed Audit Trail Logs */}
              <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs text-left">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Historical Audit Logs</h4>
                  {loadingHistory && <span className="text-[9px] font-bold text-blue-600 animate-pulse uppercase">Syncing...</span>}
                </div>

                <div className="space-y-2 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                  {feedHistory.length === 0 ? (
                    <p className="text-[11px] text-slate-400 font-medium text-center py-4">No historical ledger records on file</p>
                  ) : (
                    feedHistory.map((log) => (
                      <div key={log.id} className="flex justify-between items-center p-2.5 bg-slate-50/50 rounded-xl border border-slate-100/80 text-xs">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${log.action_type === 'add' ? 'bg-emerald-500' : 'bg-rose-500'}`}></span>
                            <span className="font-black text-slate-800 text-[11px]">{log.feed_type}</span>
                          </div>
                          <p className="text-[10px] text-slate-400 font-medium">
                            {log.adjustment_reason} • By <span className="capitalize">{log.logged_by_role.replace('_', ' ')}</span>
                          </p>
                          <p className="text-[9px] text-slate-300 font-bold">{log.formatted_date}</p>
                        </div>
                        
                        <span className={`font-black text-[11px] px-2 py-0.5 rounded-md ${
                          log.action_type === 'add' ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'
                        }`}>
                          {log.action_type === 'add' ? '+' : '-'}{log.quantity_bags} Bags
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

            </div>
          )}


          {/* 💰 SUB-VIEW: PURCHASES ACCOUNTING MANAGEMENT */}
          {activeTab === 'poultry' && poultryView === 'purchases' && (
            <div className="space-y-4 animate-fadeIn">
              {/* Back to Top Level Menu Ribbon */}
              <button
                onClick={() => setPoultryView('menu')}
                className="text-[11px] text-blue-600 hover:text-blue-700 font-black tracking-wide uppercase flex items-center gap-1 transition-all"
              >
                ← Back to Poultry Overview
              </button>

              {/* Module Header Container */}
              <div className="bg-white border border-slate-200/80 p-4 rounded-2xl flex justify-between items-center shadow-xs">
                <div>
                  <h3 className="text-xs font-black text-slate-900 uppercase tracking-wide">Procurement Expenses</h3>
                  <p className="text-[10px] text-slate-400">Log feed restocks, flock additions, equipment asset costs, and bills</p>
                </div>
              </div>

              {/* Command Action Button Trigger */}
              <div className="grid grid-cols-1">
                <button 
                  onClick={() => setIsPurchaseModalOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-[11px] tracking-wide py-3 px-4 rounded-xl transition-all shadow-xs text-center uppercase"
                >
                  💳 Record New Expense / Restock
                </button>
              </div>

              {/* Informative UI Accounting Ledger Overview Box */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs text-center">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Expense Audits Statement</h4>
                <p className="text-[11px] text-slate-400 font-medium leading-relaxed">
                  Your purchase records safely sync accounting invoices here. When buying items like feed, lodging a record here increments stock balances across your operational dashboard units seamlessly.
                </p>
              </div>
            </div>
          )}

          {/* 💉 SUB-VIEW: VACCINATION HEALTHCARE MANAGEMENT */}
          {activeTab === 'poultry' && poultryView === 'vaccination' && (
            <div className="space-y-4 animate-fadeIn">
              {/* Back to Top Level Menu Ribbon */}
              <button
                onClick={() => setPoultryView('menu')}
                className="text-[11px] text-blue-600 hover:text-blue-700 font-black tracking-wide uppercase flex items-center gap-1 transition-all"
              >
                ← Back to Poultry Overview
              </button>

              {/* Module Header Container */}
              <div className="bg-white border border-slate-200/80 p-4 rounded-2xl flex justify-between items-center shadow-xs">
                <div>
                  <h3 className="text-xs font-black text-slate-900 uppercase tracking-wide">Vaccination & Healthcare</h3>
                  <p className="text-[10px] text-slate-400">Schedule medical regimens, log flock dosages, and track biosecurity health states</p>
                </div>
              </div>

              {/* Action Trigger Buttons */}
              <div className="grid grid-cols-1">
                <button 
                  onClick={() => setIsVaccineModalOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-[11px] tracking-wide py-3 px-4 rounded-xl transition-all shadow-xs text-center uppercase"
                >
                  💉 Record Administered Vaccine
                </button>
              </div>

              {/* Informational Guidelines Alert Banner Box */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs text-center">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Medical Compliance Logs</h4>
                <p className="text-[11px] text-slate-400 font-medium leading-relaxed">
                  Flock batch histories display immunization entries here. Keeping structured schedules protects farm environments and optimizes growth compliance metrics across active tenant production cycles.
                </p>
              </div>
            </div>
          )}

          {/* 💰 SUB-VIEW: SALES & DISTRIBUTION MANAGEMENT */}
          {activeTab === 'poultry' && poultryView === 'sales' && (
            <div className="space-y-4 animate-fadeIn">
              {/* Back to Top Level Menu Ribbon */}
              <button
                onClick={() => setPoultryView('menu')}
                className="text-[11px] text-blue-600 hover:text-blue-700 font-black tracking-wide uppercase flex items-center gap-1 transition-all"
              >
                ← Back to Poultry Overview
              </button>

              {/* Module Header Container */}
              <div className="bg-white border border-slate-200/80 p-4 rounded-2xl flex justify-between items-center shadow-xs">
                <div>
                  <h3 className="text-xs font-black text-slate-900 uppercase tracking-wide">Sales & Distribution</h3>
                  <p className="text-[10px] text-slate-400">Record egg sales, bird distribution revenue, and track incoming farm cash flow</p>
                </div>
              </div>

              {/* Action Trigger Buttons */}
              <div className="grid grid-cols-1">
                <button 
                  onClick={() => setIsSalesModalOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-[11px] tracking-wide py-3 px-4 rounded-xl transition-all shadow-xs text-center uppercase"
                >
                  💰 Record New Poultry Sale
                </button>
              </div>

              {/* Informational Guidelines Alert Banner Box */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs text-center">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Revenue Tracking Statement</h4>
                <p className="text-[11px] text-slate-400 font-medium leading-relaxed">
                  Your distribution transactions instantly compile financial metrics here. When selling inventory like birds, logging a record here will seamlessly communicate data to reduce active bird totals across your production cycle sheets.
                </p>
              </div>
            </div>
          )}

          {/* 🌽 CROPS PRODUCTION TRACKING PANELS */}
          {activeTab === 'crops' && (
            <CropsHub 
              shopId={shopId || '81'} 
              farmName={farmName} 
              userSession={userSession}
              isCropsModalOpen={isCropsModalOpen}
              setIsCropsModalOpen={setIsCropsModalOpen}
              // PASS THESE PARAMETERS FROM THE PARENT MEMORY TO RESOLVE TS2740 👇
              cropClass={cropClass}
              cropVariety={cropVariety}
              acreage={acreage}
              cropStartDate={cropStartDate}
              harvestDate={harvestDate}
              setCropClass={setCropClass}
              setCropVariety={setCropVariety}
              setAcreage={setAcreage}
              setCropStartDate={setCropStartDate}
            />
          )}

          {/* 🐄 LIVESTOCK INVENTORY TRACKING PANELS */}
          {activeTab === 'livestock' && (
            <LivestockHub 
              shopId={shopId}
              farmName={farmName}
              userSession={userSession}
            />
          )}


        </div>
      )}

      {/* 📱 THE DYNAMIC INPUT MODAL GRID SYSTEM (Mirrors the reference layout specifications) */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-end justify-center z-50 p-4">
          <div className="w-full bg-white rounded-3xl p-5 shadow-xl max-w-md border border-slate-100 flex flex-col space-y-4 animate-slideUp">
            
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-black text-blue-900 tracking-wide">Configure Production Cycle</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 font-bold hover:text-slate-600 text-xs">Cancel</button>
            </div>

            <form onSubmit={handleSaveCycle} className="space-y-4 text-left">
              {/* 1. Product Field Option Select Input Container */}
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Product</label>
                <select 
                  value={modalProduct} 
                  onChange={(e) => handleProductChange(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none focus:border-blue-500"
                >
                  <option value="Broiler">Broiler - Day Old Chicks</option>
                  <option value="Layers">Layers - Day Old Chicks</option>
                  <option value="Kenbro">Kenbro - Day Old Chicks</option>
                  <option value="Geese">Geese - Production Stock</option>
                  <option value="Turkey">Turkey - Production Stock</option>
                </select>
              </div>

              {/* 2. Volume Field Content Input Block Container */}
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Number of Chicks</label>
                <input 
                  type="number" 
                  placeholder="Enter total volume count" 
                  value={chickCount} 
                  onChange={e => setChickCount(e.target.value)}
                  required 
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none" 
                />
              </div>

              {/* 3. Operational Start Date Metric Log Parameter */}
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Start Date</label>
                <input 
                  type="date" 
                  value={startDate} 
                  onChange={e => setStartDate(e.target.value)}
                  required 
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none" 
                />
              </div>

              {/* 4. Automated Maturity Metric Field Elements Block Container */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Production Days</label>
                  <input 
                    type="number" 
                    value={productionDays} 
                    onChange={e => setProductionDays(parseInt(e.target.value) || 0)}
                    required 
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-100 text-slate-500 font-bold focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Maturity Date</label>
                  <input 
                    type="date" 
                    value={maturityDate} 
                    readOnly
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-100 text-slate-500 font-bold focus:outline-none" 
                  />
                </div>
              </div>

              {/* 5. Execution Action Command Submit Trigger Bar */}
              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-blue-500 hover:bg-blue-600 active:scale-95 text-white font-bold p-3.5 rounded-xl text-sm tracking-wide transition-all shadow-md pt-3"
              >
                {loading ? 'Saving Parameters...' : 'SAVE CYCLE'}
              </button>
            </form>
          </div>
        </div>
      )}
   

      {/* 📱 THE DYNAMIC LIVESTOCK INPUT MODAL */}
      {isLiveModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-end justify-center z-50 p-4">
          <div className="w-full bg-white rounded-3xl p-5 shadow-xl max-w-md border border-slate-100 flex flex-col space-y-4 animate-slideUp">
            
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-black text-blue-900 tracking-wide">Log Livestock Updates</h3>
              <button onClick={() => setIsLiveModalOpen(false)} className="text-slate-400 font-bold hover:text-slate-600 text-xs">Cancel</button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              setLoading(true);
              try {
                const response = await fetch('https://n8n.tenear.com/webhook/update-livestock', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    action: 'save_livestock_inventory',
                    shop_id: parseInt(shopId || '81'),
                    phone_number: localStorage.getItem('remembered_phone_number'),
                    animal_class: animalClass,
                    breed_variety: breedVariety,
                    head_count: parseInt(headCount),
                    health_status: healthStatus
                  })
                });
                if (response.ok) {
                  alert(`Successfully updated inventory for ${headCount} ${breedVariety} Head!`);
                  setIsLiveModalOpen(false);
                  setHeadCount('');
                }
              } catch(err) { console.error(err); }
              finally { setLoading(false); }
            }} className="space-y-4 text-left">
              
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Animal Class</label>
                <select value={animalClass} onChange={(e) => handleAnimalClassChange(e.target.value as any)} className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none">
                  <option value="Cattle">🐄 Cattle (Dairy / Beef)</option>
                  <option value="Goats">🐐 Goats (Dairy / Meat)</option>
                  <option value="Sheep">🐑 Sheep (Wool / Mutton)</option>
                </select>
              </div>

              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Breed / Variety</label>
                <select value={breedVariety} onChange={(e) => setBreedVariety(e.target.value)} className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none">
                  {animalClass === 'Cattle' && (
                    <>
                      <option value="Friesian">Friesian</option>
                      <option value="Ayrshire">Ayrshire</option>
                      <option value="Guernsey">Guernsey</option>
                      <option value="Jersey">Jersey</option>
                      <option value="Boran">Boran</option>
                      <option value="Sahiwal">Sahiwal</option>
                      <option value="Other Cattle">Other Breed</option>
                    </>
                  )}
                  {animalClass === 'Goats' && (
                    <>
                      <option value="Boer">Boer</option>
                      <option value="Toggenburg">Toggenburg</option>
                      <option value="Saanen">Saanen</option>
                      <option value="Alpine">Alpine</option>
                      <option value="Galla">Galla</option>
                      <option value="Other Goats">Other Breed</option>
                    </>
                  )}
                  {animalClass === 'Sheep' && (
                    <>
                      <option value="Dorper">Dorper</option>
                      <option value="Red Maasai">Red Maasai</option>
                      <option value="Merino">Merino</option>
                      <option value="Corriedale">Corriedale</option>
                      <option value="Other Sheep">Other Breed</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Total Head Count</label>
                <input type="number" placeholder="Enter number of animals" value={headCount} onChange={e => setHeadCount(e.target.value)} required className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none" />
              </div>

              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Current Health Status</label>
                <select value={healthStatus} onChange={(e) => setHealthStatus(e.target.value)} className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none">
                  <option value="Healthy">🟢 Healthy & Productive</option>
                  <option value="Treatment">🟡 Under Medical Treatment</option>
                  <option value="Quarantined">🔴 Quarantined / Isolated</option>
                </select>
              </div>

              <button type="submit" disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold p-3.5 rounded-xl text-sm tracking-wide transition-all shadow-md">
                {loading ? 'Saving Data...' : 'SAVE LIVESTOCK RECORDS'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 📱 THE DYNAMIC INVENTORY ADJUSTMENT MODAL OVERLAY */}
      {isAdjustModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-end justify-center z-50 p-4 animate-fadeIn">
          <div className="w-full bg-white rounded-3xl p-5 shadow-xl max-w-md border border-slate-100 flex flex-col space-y-4 animate-slideUp">
            
            {/* Modal Header */}
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-black text-slate-900 tracking-wide uppercase">Adjust Feed Inventory</h3>
              <button 
                onClick={() => setIsAdjustModalOpen(false)} 
                className="text-slate-400 font-bold hover:text-slate-600 text-xs uppercase"
              >
                Cancel
              </button>
            </div>

            {/* Adjustment Form Layout */}
            <form onSubmit={handleAdjustInventory} className="space-y-4 text-left">
              
              {/* 1. Feed Stock Type Selection Dropdown */}
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Select Feed Variety</label>
                <select 
                  value={adjustFeedType} 
                  onChange={(e) => setAdjustFeedType(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none focus:border-blue-500"
                >
                  <option value="Starter Crumbs">Starter Crumbs</option>
                  <option value="Growers Pellets">Growers Pellets</option>
                  <option value="Finisher Pellets">Finisher Pellets</option>
                  <option value="Layers Mash">Layers Mash</option>
                </select>
              </div>

              {/* 2. Adjustment Mode Selector Toggles */}
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Adjustment Direction</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setAdjustAction('add')}
                    className={`py-2 px-3 text-xs font-bold rounded-xl border text-center transition-all ${
                      adjustAction === 'add' 
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs' 
                        : 'bg-slate-50 text-slate-600 border-slate-200'
                    }`}
                  >
                    ➕ Stock Add / Correction
                  </button>
                  <button
                    type="button"
                    onClick={() => setAdjustAction('remove')}
                    className={`py-2 px-3 text-xs font-bold rounded-xl border text-center transition-all ${
                      adjustAction === 'remove' 
                        ? 'bg-rose-600 text-white border-rose-600 shadow-xs' 
                        : 'bg-slate-50 text-slate-600 border-slate-200'
                    }`}
                  >
                    ➖ Spillage / Removal
                  </button>
                </div>
              </div>

              {/* 3. Volumetric Quantity Metric Input Field */}
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Quantity (Bags)</label>
                <input 
                  type="number" 
                  step="0.1"
                  placeholder="Enter volume count to adjust" 
                  value={adjustQuantity} 
                  onChange={e => setAdjustQuantity(e.target.value)}
                  required 
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none focus:border-blue-500" 
                />
              </div>

              {/* 4. Categorized Audit Reason Selection Dropdown */}
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Reason for Entry</label>
                <select 
                  value={adjustReason} 
                  onChange={(e) => setAdjustReason(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none focus:border-blue-500"
                >
                  <option value="Spillage/Waste">Spillage / Feeding Waste</option>
                  <option value="Silo Audit Correction">Physical Silo Count Correction</option>
                  <option value="Damaged Stock">Damaged / Wet Feed Bag Disposal</option>
                  <option value="Internal Transfer">Transfer to Another Production Shed</option>
                </select>
              </div>

              {/* 5. Command Action Submission Bar */}
              <button 
                type="submit" 
                disabled={loading}
                className={`w-full text-white font-bold p-3.5 rounded-xl text-sm tracking-wide transition-all shadow-md uppercase ${
                  adjustAction === 'add' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'
                }`}
              >
                {loading ? 'Processing Update...' : `Confirm Inventory ${adjustAction === 'add' ? 'Addition' : 'Deduction'}`}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 📱 THE DYNAMIC LOG DAILY FEEDING MODAL OVERLAY */}
      {isFeedModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-end justify-center z-50 p-4 animate-fadeIn">
          <div className="w-full bg-white rounded-3xl p-5 shadow-xl max-w-md border border-slate-100 flex flex-col space-y-4 animate-slideUp">
            
            {/* Modal Header */}
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-black text-blue-900 tracking-wide uppercase">Log Daily Bird Feeding</h3>
              <button 
                onClick={() => setIsFeedModalOpen(false)} 
                className="text-slate-400 font-bold hover:text-slate-600 text-xs uppercase"
              >
                Cancel
              </button>
            </div>

            {/* Input Form Fields */}
            <form onSubmit={handleLogDailyFeeding} className="space-y-4 text-left">
              
              {/* 1. Feed Mix Type Selection Dropdown */}
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Select Feed Administered</label>
                <select 
                  value={feedLogType} 
                  onChange={(e) => setFeedLogType(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none focus:border-blue-500"
                >
                  <option value="Starter Crumbs">Starter Crumbs</option>
                  <option value="Growers Pellets">Growers Pellets</option>
                  <option value="Finisher Pellets">Finisher Pellets</option>
                  <option value="Layers Mash">Layers Mash</option>
                </select>
              </div>

              {/* 2. Volume Quantity Count Input */}
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Quantity Consumed (Bags)</label>
                <input 
                  type="number" 
                  step="0.01"
                  placeholder="How many bags were served?" 
                  value={feedLogQuantity} 
                  onChange={e => setFeedLogQuantity(e.target.value)}
                  required 
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none focus:border-blue-500" 
                />
              </div>

              {/* 3. Shed / Unit Target Identifier Input */}
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Target House / Shed</label>
                <select 
                  value={feedLogShed} 
                  onChange={(e) => setFeedLogShed(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none focus:border-blue-500"
                >
                  <option value="Shed 1">Poultry Shed A (Broilers)</option>
                  <option value="Shed 2">Poultry Shed B (Layers)</option>
                  <option value="Hatchery Unit">Hatchery / Incubator Room</option>
                </select>
              </div>

              {/* 4. Action Command Submission Button */}
              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold p-3.5 rounded-xl text-sm tracking-wide transition-all shadow-md uppercase"
              >
                {loading ? 'Transmitting Data Logs...' : 'Save Consumption Entry'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 📱 THE DYNAMIC PROCUREMENT PURCHASES INPUT MODAL OVERLAY */}
      {isPurchaseModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-end justify-center z-50 p-4 animate-fadeIn">
          <div className="w-full bg-white rounded-3xl p-5 shadow-xl max-w-md border border-slate-100 flex flex-col space-y-4 animate-slideUp">
            
            {/* Modal Header */}
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-black text-slate-900 tracking-wide uppercase">Record Procurement Item</h3>
              <button 
                onClick={() => setIsPurchaseModalOpen(false)} 
                className="text-slate-400 font-bold hover:text-slate-600 text-xs uppercase"
              >
                Cancel
              </button>
            </div>

            {/* Procurement Entry Input Forms */}
            <form onSubmit={handleLogPurchase} className="space-y-4 text-left">
              
              {/* 1. Category Classification Pick */}
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Item Sector Category</label>
                <select 
                  value={purchaseCategory} 
                  onChange={(e) => {
                    setPurchaseCategory(e.target.value);
                    if (e.target.value === 'Feed') setPurchaseDescription('Starter Crumbs');
                    else if (e.target.value === 'Chicks') setPurchaseDescription('Broiler Chicks');
                    else setPurchaseDescription('');
                  }}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none focus:border-blue-500"
                >
                  <option value="Feed">Poultry Feed Stock</option>
                  <option value="Chicks">Day Old Chicks / Stock</option>
                  <option value="Vaccines">Medical / Vaccines</option>
                  <option value="Implements">Farm Tools & Hardware</option>
                </select>
              </div>

              {/* 2. Contextual Description Input Selection Dropdown */}
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Item Name / Description</label>
                {purchaseCategory === 'Feed' ? (
                  <select 
                    value={purchaseDescription} 
                    onChange={(e) => setPurchaseDescription(e.target.value)}
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none"
                  >
                    <option value="Starter Crumbs">Starter Crumbs</option>
                    <option value="Growers Pellets">Growers Pellets</option>
                    <option value="Finisher Pellets">Finisher Pellets</option>
                    <option value="Layers Mash">Layers Mash</option>
                  </select>
                ) : (
                  <input 
                    type="text" 
                    placeholder="e.g., Broiler Chicks, Automatic Drinkers" 
                    value={purchaseDescription} 
                    onChange={e => setPurchaseDescription(e.target.value)}
                    required 
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none" 
                  />
                )}
              </div>

              {/* 3. Numerical Quantity and Unit Pricing Grid Layout Containers */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Quantity (Units)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    placeholder="Amount" 
                    value={purchaseQuantity} 
                    onChange={e => setPurchaseQuantity(e.target.value)}
                    required 
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Unit Price ($)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    placeholder="Cost per unit" 
                    value={purchaseUnitPrice} 
                    onChange={e => setPurchaseUnitPrice(e.target.value)}
                    required 
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none" 
                  />
                </div>
              </div>

              {/* 4. Optional Metadata Tracking Fields (Supplier and Receipt Reference ID Contexts) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Supplier Store</label>
                  <input 
                    type="text" 
                    placeholder="Store/Vendor" 
                    value={purchaseSupplier} 
                    onChange={e => setPurchaseSupplier(e.target.value)}
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Invoice / Receipt #</label>
                  <input 
                    type="text" 
                    placeholder="Ref ID" 
                    value={purchaseReceipt} 
                    onChange={e => setPurchaseReceipt(e.target.value)}
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none" 
                  />
                </div>
              </div>

              {/* 5. Process Confirmation Button Action Bar Row */}
              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold p-3.5 rounded-xl text-sm tracking-wide transition-all shadow-md uppercase"
              >
                {loading ? 'Transmitting Invoices...' : 'Save Procurement Bill'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 📱 THE DYNAMIC FLOCK VACCINATION INPUT MODAL OVERLAY */}
      {isVaccineModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-end justify-center z-50 p-4 animate-fadeIn">
          <div className="w-full bg-white rounded-3xl p-5 shadow-xl max-w-md border border-slate-100 flex flex-col space-y-4 animate-slideUp">
            
            {/* Modal Header */}
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-black text-slate-900 tracking-wide uppercase">Log Vaccine Administration</h3>
              <button 
                onClick={() => setIsVaccineModalOpen(false)} 
                className="text-slate-400 font-bold hover:text-slate-600 text-xs uppercase"
              >
                Cancel
              </button>
            </div>

            {/* Input Form Controls */}
            <form onSubmit={handleLogVaccination} className="space-y-4 text-left">
              
              {/* 1. Vaccine Type Picker */}
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Vaccine Name</label>
                <select 
                  value={vaccineName} 
                  onChange={(e) => setVaccineName(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none focus:border-blue-500"
                >
                  <option value="Gumboro">Gumboro (IBD)</option>
                  <option value="Newcastle">Newcastle Disease (ND)</option>
                  <option value="Fowl Pox">Fowl Pox</option>
                  <option value="Marek Disease">Marek's Disease</option>
                </select>
              </div>

              {/* 2. Target Dynamic Flock Classification */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Target Flock</label>
                  <select 
                    value={vaccineFlockType} 
                    onChange={(e) => setVaccineFlockType(e.target.value)}
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none"
                  >
                    <option value="Broilers">Broilers</option>
                    <option value="Layers">Layers</option>
                    <option value="Kenbros">Kenbros</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Admin Method</label>
                  <select 
                    value={vaccineMethod} 
                    onChange={(e) => setVaccineMethod(e.target.value)}
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none"
                  >
                    <option value="Drinking Water">Drinking Water</option>
                    <option value="Eye Drop">Eye Drop</option>
                    <option value="Subcutaneous Injection">Injection</option>
                  </select>
                </div>
              </div>

              {/* 3. Dosage metric and headcounts calculation row grid */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Dose / Bird (ml/drops)</label>
                  <input 
                    type="number" 
                    step="0.001"
                    placeholder="e.g. 0.5" 
                    value={vaccineDosage} 
                    onChange={e => setVaccineDosage(e.target.value)}
                    required 
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Birds Treated</label>
                  <input 
                    type="number" 
                    placeholder="Headcount" 
                    value={vaccineBirdsTreated} 
                    onChange={e => setVaccineBirdsTreated(e.target.value)}
                    required 
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none" 
                  />
                </div>
              </div>

              {/* 4. Scheduling Context Parameters */}
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Scheduled Strategy Date</label>
                <input 
                  type="date" 
                  value={vaccineScheduleDate} 
                  onChange={e => setVaccineScheduleDate(e.target.value)}
                  required 
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none" 
                />
              </div>

              {/* 5. Process Submission Fire Bar */}
              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold p-3.5 rounded-xl text-sm tracking-wide transition-all shadow-md uppercase"
              >
                {loading ? 'Logging Health Records...' : 'Save Vaccination Entry'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 📱 THE DYNAMIC POULTRY SALES INPUT MODAL OVERLAY */}
      {isSalesModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-end justify-center z-50 p-4 animate-fadeIn">
          <div className="w-full bg-white rounded-3xl p-5 shadow-xl max-w-md border border-slate-100 flex flex-col space-y-4 animate-slideUp">
            
            {/* Modal Header */}
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-black text-slate-900 tracking-wide uppercase">Log Poultry Sale</h3>
              <button 
                onClick={() => setIsSalesModalOpen(false)} 
                className="text-slate-400 font-bold hover:text-slate-600 text-xs uppercase"
              >
                Cancel
              </button>
            </div>

            {/* Input Form Controls */}
            <form onSubmit={handleLogSale} className="space-y-4 text-left">
              
              -- 1. Product Type Picker --
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Product Category</label>
                <select 
                  value={salesProductType} 
                  onChange={(e) => setSalesProductType(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none focus:border-blue-500"
                >
                  <option value="Eggs">Eggs (Crates)</option>
                  <option value="Live Birds">Live Birds (Headcount)</option>
                  <option value="Processed Meat">Processed Meat (KG)</option>
                </select>
              </div>

              -- 2. Numerical Quantity and Unit Pricing Grid --
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">
                    {salesProductType === 'Eggs' ? 'Quantity (Crates)' : salesProductType === 'Processed Meat' ? 'Quantity (KG)' : 'Quantity (Birds)'}
                  </label>
                  <input 
                    type="number" 
                    step="0.01"
                    placeholder="Amount" 
                    value={salesQuantity} 
                    onChange={e => setSalesQuantity(e.target.value)}
                    required 
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none focus:border-blue-500" 
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Unit Price ($)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    placeholder="Cost per unit" 
                    value={salesUnitPrice} 
                    onChange={e => setSalesUnitPrice(e.target.value)}
                    required 
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none focus:border-blue-500" 
                  />
                </div>
              </div>

              -- 3. Customer and Payment Method Parameters --
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Customer Name</label>
                  <input 
                    type="text" 
                    placeholder="e.g. Local Hotel" 
                    value={salesCustomer} 
                    onChange={e => setSalesCustomer(e.target.value)}
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 font-bold focus:outline-none focus:border-blue-500" 
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Payment Channel</label>
                  <select 
                    value={salesPaymentMethod} 
                    onChange={(e) => setSalesPaymentMethod(e.target.value)}
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-slate-50 font-bold text-slate-700 focus:outline-none"
                  >
                    <option value="Mobile Money">Mobile Money</option>
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                  </select>
                </div>
              </div>

              -- 4. Process Submission Bar --
              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold p-3.5 rounded-xl text-sm tracking-wide transition-all shadow-md uppercase"
              >
                {loading ? 'Transmitting Transaction...' : 'Save Sales Record'}
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
